import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { kuniriIconWeight, KuniriIcons } from '@/lib/icons'

import type { PrivateProfile } from '../types'
import { Notice } from './notice'

export type ProfileCheck =
  | { status: 'loading' }
  | { status: 'ready'; profile: PrivateProfile }
  | { status: 'error' }

interface ProfileGateProps {
  check: ProfileCheck
  onRetry: () => void
}

/**
 * Create requires an avatar (the API enforces it). The check is rendered next
 * to the basic fields so the reason a publish is blocked is always visible and
 * the user has a direct link to the profile.
 */
export function ProfileGate({ check, onRetry }: ProfileGateProps) {
  const { t } = useTranslation()

  if (check.status === 'loading') {
    return (
      <p
        role="status"
        className="flex items-center gap-2 text-sm text-muted"
      >
        <span className="spinner" aria-hidden="true" />
        {t('activityForm.profile.checking')}
      </p>
    )
  }

  if (check.status === 'error') {
    return (
      <div className="space-y-2">
        <Notice tone="error">{t('activityForm.profile.checkError')}</Notice>
        <button
          type="button"
          className="btn-ghost min-h-11 px-2 text-sm"
          onClick={onRetry}
        >
          <KuniriIcons.arrowClockwise
            size={17}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {t('activityForm.profile.retry')}
        </button>
      </div>
    )
  }

  if (check.profile.avatarUrl) return null

  return (
    <Notice tone="info">
      <p className="font-body text-base font-bold text-deep">
        {t('activityForm.profile.title')}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-muted">
        {t('activityForm.profile.body')}
      </p>
      <Link
        to="/profile"
        className="mt-2 inline-flex min-h-11 items-center font-bold text-teal underline"
      >
        {t('activityForm.profile.action')}
      </Link>
      <div>
        <button
          type="button"
          className="btn-ghost mt-1 min-h-11 px-2 text-sm"
          onClick={onRetry}
        >
          <KuniriIcons.arrowClockwise
            size={17}
            weight={kuniriIconWeight}
            aria-hidden="true"
          />
          {t('activityForm.profile.retry')}
        </button>
      </div>
    </Notice>
  )
}
